// React
import { ReactElement, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Modules
import { MixedCheckbox } from '@reach/checkbox';
import classNames from 'classnames/bind';
import VisuallyHidden from '@reach/visually-hidden';
// Uitls
import { getColorStyle } from '@utmb/utils/colors';
import { getGender } from '@utmb/utils/gender';
import { rem } from '@utmb/utils/font';
// Design System
import { DropDown, DropDownItem } from '@utmb/design-system/dropdown/Dropdown';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Separator } from '@utmb/design-system/separator/Separator';
// Interfaces
import { ColorTypesEnum } from '@utmb/types/colors';
import { IconsEnum } from '@utmb/types/icons';
import { IndexRankingTableMobileFiltersProps } from '@utmb/types/runner-ranking-table';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { SexEnum } from '@utmb/types/enum/SexEnum';
// Styles
import styles from './mobile-filters.module.scss';

const cx = classNames.bind(styles);

const MobileFilters = (props: IndexRankingTableMobileFiltersProps): ReactElement => {
    const { bgColor, currentAgeGroup, currentCategory, currentGender, ageGroups, query, setCurrentGender, setCurrentAgeGroup, fetchData } = props;

    const genders = [SexEnum.MEN, SexEnum.WOMEN];

    const { t } = useTranslation();

    const [isPanelOpen, setIsPanelOpen] = useState(false);

    const handleInputAgeGroupChange = (age: string): void => {
        if (currentAgeGroup === age) {
            setCurrentAgeGroup('');
            query.ageGroup = '';
        } else {
            setCurrentAgeGroup(age);
            query.ageGroup = age;
        }
    };

    const handleInputGenderChange = (gender: string): void => {
        if (currentGender === gender) {
            setCurrentGender('');
            query.sex = '';
        } else {
            setCurrentGender(gender);
            query.sex = gender;
        }
    };

    const handleSubmitSearch = (): void => {
        setIsPanelOpen(false);
        fetchData(query);
    };

    return (
        <>
            <div className={styles.root}>
                <button className={styles.toggler_panel} onClick={() => setIsPanelOpen(!isPanelOpen)}>
                    <Font.FuturaHeavy mobile="16" desktop="16">
                        Filters
                    </Font.FuturaHeavy>

                    <Icon
                        color={currentCategory === PiCategoryEnum.PI100M ? 'var(--utmb-color-white)' : 'var(--utmb-color-ws-primary)'}
                        variant={IconsEnum[isPanelOpen ? 'CHEVRON_UP' : 'CHEVRON_DOWN']}
                    />
                </button>

                <div
                    className={cx(styles.filter_panel_container, bgColor, {
                        filter_panel_container_open: isPanelOpen,
                    })}
                >
                    <div className={styles.filter_panel}>
                        {ageGroups.length && (
                            <div
                                className={styles.toolbar}
                                style={{
                                    borderColor:
                                        currentCategory === PiCategoryEnum.PI100M
                                            ? getColorStyle(ColorTypesEnum.WHITE)
                                            : getColorStyle(ColorTypesEnum.WS_PRIMARY),
                                }}
                            >
                                <DropDown
                                    className={styles.drop}
                                    button={({ isExpanded }) => (
                                        <>
                                            <Font.FuturaHeavy mobile="16" desktop="16">
                                                {currentAgeGroup !== '' ? currentAgeGroup : t('performanceTable.ageGroup')}
                                            </Font.FuturaHeavy>
                                            <Icon
                                                color={currentCategory === PiCategoryEnum.PI100M ? 'var(--utmb-color-white)' : 'var(--utmb-color-ws-primary)'}
                                                variant={IconsEnum[isExpanded ? 'CHEVRON_UP' : 'CHEVRON_DOWN']}
                                            />
                                        </>
                                    )}
                                >
                                    {ageGroups?.map((age: string, index: number) => {
                                        const checked = age === currentAgeGroup;

                                        const hasSeparator = index < ageGroups.length - 1;

                                        return (
                                            <div key={index} className={styles.item_wrapper}>
                                                <DropDownItem onSelect={() => handleInputAgeGroupChange(age)} className={styles.item}>
                                                    <Font.OxaniumMedium mobile="12" desktop="14" as="span">
                                                        {age}
                                                    </Font.OxaniumMedium>
                                                    <label>
                                                        <MixedCheckbox value={age} checked={checked} />
                                                        {checked && <Icon variant={IconsEnum.CHECKBOX} color="rgba(65, 80, 92,.8)" />}
                                                        <VisuallyHidden> {age}</VisuallyHidden>
                                                    </label>
                                                </DropDownItem>
                                                {hasSeparator && (
                                                    <Separator color="rgba(65, 80, 92,.2)" spacing="0 auto" depth={`calc(100% - ${rem('15px')})`} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </DropDown>
                            </div>
                        )}

                        {genders.length && (
                            <div
                                className={styles.toolbar}
                                style={{
                                    borderColor:
                                        currentCategory === PiCategoryEnum.PI100M
                                            ? getColorStyle(ColorTypesEnum.WHITE)
                                            : getColorStyle(ColorTypesEnum.WS_PRIMARY),
                                }}
                            >
                                <DropDown
                                    className={styles.drop}
                                    button={({ isExpanded }) => (
                                        <>
                                            <Font.FuturaHeavy mobile="16" desktop="16">
                                                {currentGender !== '' ? t(getGender(currentGender)) : t('common.gender.label')}
                                            </Font.FuturaHeavy>
                                            <Icon
                                                color={currentCategory === PiCategoryEnum.PI100M ? 'var(--utmb-color-white)' : 'var(--utmb-color-ws-primary)'}
                                                variant={IconsEnum[isExpanded ? 'CHEVRON_UP' : 'CHEVRON_DOWN']}
                                            />
                                        </>
                                    )}
                                >
                                    {genders.map((gender: string, index: number) => {
                                        const checked = gender === currentGender;

                                        const hasSeparator = index < genders.length - 1;

                                        return (
                                            <div key={index} className={styles.item_wrapper}>
                                                <DropDownItem onSelect={() => handleInputGenderChange(gender)} className={styles.item}>
                                                    <Font.OxaniumMedium mobile="12" desktop="14" as="span">
                                                        {t(getGender(gender))}
                                                    </Font.OxaniumMedium>
                                                    <label>
                                                        <MixedCheckbox value={gender} checked={true} />
                                                        {checked && <Icon variant={IconsEnum.CHECKBOX} color="rgba(65, 80, 92,.8)" />}
                                                        <VisuallyHidden> {gender}</VisuallyHidden>
                                                    </label>
                                                </DropDownItem>
                                                {hasSeparator && (
                                                    <Separator color="rgba(65, 80, 92,.2)" spacing="0 auto" depth={`calc(100% - ${rem('15px')})`} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </DropDown>
                            </div>
                        )}
                        <button onClick={() => handleSubmitSearch()} className={styles.filters_cta}>
                            search
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MobileFilters;
